// Cache utility with TTL support and size limits

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
  size?: number;
}

interface CacheConfig {
  maxSize?: number; // Maximum cache size in MB
  defaultTTL?: number; // Default TTL in milliseconds
  cleanupInterval?: number; // Cleanup interval in milliseconds
}

class CacheManager {
  private cache = new Map<string, CacheItem<unknown>>();
  private config: CacheConfig;
  private currentSize = 0;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(config: CacheConfig = {}) {
    this.config = {
      maxSize: 50, // 50MB default
      defaultTTL: 5 * 60 * 1000, // 5 minutes default
      cleanupInterval: 60 * 1000, // 1 minute cleanup
      ...config
    };

    // Start cleanup timer
    this.startCleanup();
  }

  /**
   * Store an item in cache
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL!,
      size: this.calculateSize(data)
    };

    // Remove existing item if it exists
    if (this.cache.has(key)) {
      const existingItem = this.cache.get(key)!;
      this.currentSize -= existingItem.size || 0;
    }

    // Check size limits
    if (this.config.maxSize && item.size) {
      const newSize = this.currentSize + item.size;
      if (newSize > this.config.maxSize * 1024 * 1024) {
        this.evictLRU(item.size);
      }
    }

    this.cache.set(key, item);
    this.currentSize += item.size || 0;
  }

  /**
   * Get an item from cache
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.delete(key);
      return null;
    }

    return item.data as T;
  }

  /**
   * Delete an item from cache
   */
  delete(key: string): boolean {
    const item = this.cache.get(key);
    if (item) {
      this.currentSize -= item.size || 0;
      return this.cache.delete(key);
    }
    return false;
  }

  /**
   * Check if an item exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.currentSize = 0;
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    count: number;
    maxSize: number;
    hitRate?: number;
  } {
    return {
      size: Math.round(this.currentSize / 1024 / 1024 * 100) / 100, // MB
      count: this.cache.size,
      maxSize: this.config.maxSize || 0
    };
  }

  /**
   * Get or set with a factory function
   */
  async getOrSet<T>(
    key: string, 
    factory: () => Promise<T> | T, 
    ttl?: number
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const data = await factory();
    this.set(key, data, ttl);
    return data;
  }

  /**
   * Set cache with versioning
   */
  setVersioned<T>(key: string, data: T, version: string, ttl?: number): void {
    const versionedKey = `${key}:v${version}`;
    this.set(versionedKey, data, ttl);
    
    // Clean up old versions
    this.cleanupVersions(key, version);
  }

  /**
   * Get cache with versioning
   */
  getVersioned<T>(key: string, version: string): T | null {
    const versionedKey = `${key}:v${version}`;
    return this.get<T>(versionedKey);
  }

  private calculateSize(data: unknown): number {
    try {
      const jsonString = JSON.stringify(data);
      return new Blob([jsonString]).size;
    } catch {
      return 1024; // Default 1KB if can't calculate
    }
  }

  private evictLRU(requiredSize: number): void {
    // Sort by timestamp (oldest first)
    const entries = Array.from(this.cache.entries()).sort(
      ([, a], [, b]) => a.timestamp - b.timestamp
    );

    let freedSize = 0;
    for (const [key] of entries) {
      const item = this.cache.get(key)!;
      freedSize += item.size || 0;
      this.cache.delete(key);
      this.currentSize -= item.size || 0;

      if (freedSize >= requiredSize) {
        break;
      }
    }
  }

  private cleanupVersions(baseKey: string, currentVersion: string): void {
    const keysToDelete: string[] = [];
    
    Array.from(this.cache.keys()).forEach(key => {
      if (key.startsWith(`${baseKey}:v`) && !key.endsWith(`:v${currentVersion}`)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.delete(key));
  }

  private startCleanup(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanupExpired();
    }, this.config.cleanupInterval);
  }

  private cleanupExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    Array.from(this.cache.entries()).forEach(([key, item]) => {
      if (now - item.timestamp > item.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.delete(key));
  }

  /**
   * Delete all entries whose key matches the predicate
   */
  deleteByKey(predicate: (key: string) => boolean): void {
    const keysToDelete: string[] = [];
    Array.from(this.cache.keys()).forEach((key) => {
      if (predicate(key)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this.delete(key));
  }

  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.clear();
  }
}

// Create cache instances for different data types
export const userSettingsCache = new CacheManager({
  maxSize: 5, // 5MB
  defaultTTL: 10 * 60 * 1000 // 10 minutes
});

export const projectMetadataCache = new CacheManager({
  maxSize: 10, // 10MB
  defaultTTL: 5 * 60 * 1000 // 5 minutes
});

export const aiModelConfigCache = new CacheManager({
  maxSize: 1, // 1MB
  defaultTTL: 30 * 60 * 1000 // 30 minutes
});

export const sessionCache = new CacheManager({
  maxSize: 20, // 20MB
  defaultTTL: 60 * 60 * 1000 // 1 hour
});

// Utility functions for specific cache operations
export class CacheUtils {
  static generateKey(...parts: string[]): string {
    return parts.join(':');
  }

  static generateUserKey(userId: string, type: string): string {
    return `user:${userId}:${type}`;
  }

  /**
   * Cache key for one project's data, scoped to the VIEWING USER.
   *
   * The `user:` prefix is load-bearing twice over, and its absence caused a
   * real cross-student leak (fixed 2026-09-05):
   *
   *  1. The key used to be `project:${projectId}:${type}` — identical for every
   *     user. On a shared classroom device, student A opening a private project
   *     cached it; student B, logging in afterwards on the same browser, got a
   *     cache HIT and `projectService.getProject` returns cached data BEFORE
   *     its access check. Student B saw student A's private project.
   *  2. `clearUserCache(userId)` deletes keys containing `user:${userId}`.
   *     Project keys never contained that, so every project-cache invalidation
   *     in this codebase silently did nothing.
   *
   * Scoping the key to the user fixes both at once.
   */
  static generateProjectKey(userId: string, projectId: string, type: string): string {
    return `user:${userId}:project:${projectId}:${type}`;
  }

  // Cache warming — re-touches existing entries to extend their TTL and
  // ensures per-project slots are primed ahead of first access.
  static async warmCache(userId: string, projectIds: string[]): Promise<void> {
    // Re-touch any already-cached project data so it stays warm longer
    projectIds.forEach((id) => {
      const key = CacheUtils.generateProjectKey(userId, id, 'data');
      const existing = projectMetadataCache.get(key);
      if (existing !== null) {
        projectMetadataCache.set(key, existing); // resets TTL timestamp
      }
    });

    // Re-touch user settings if already cached
    const userSettingsKey = CacheUtils.generateUserKey(userId, 'settings');
    const cachedSettings = userSettingsCache.get(userSettingsKey);
    if (cachedSettings !== null) {
      userSettingsCache.set(userSettingsKey, cachedSettings);
    }
  }

  // Clear user-specific cache entries across all cache instances
  static clearUserCache(userId: string): void {
    const prefix = `user:${userId}`;
    [userSettingsCache, projectMetadataCache, sessionCache].forEach((cache) => {
      cache.deleteByKey((key) => key.includes(prefix));
    });
  }

  /**
   * Drop every cached entry, for every user.
   *
   * Called on sign-out. K-12 runs on shared devices — one Chromebook, many
   * students through the day — so anything left in memory after a logout is
   * readable by the next child to sign in. Scoped keys stop a cross-user cache
   * HIT; this stops the data being resident at all.
   */
  static clearAll(): void {
    [userSettingsCache, projectMetadataCache, sessionCache].forEach((cache) => cache.clear());
  }

  // Get cache status for debugging
  static getCacheStatus(): {
    userSettings: ReturnType<CacheManager['getStats']>;
    projectMetadata: ReturnType<CacheManager['getStats']>;
    aiModelConfig: ReturnType<CacheManager['getStats']>;
    session: ReturnType<CacheManager['getStats']>;
  } {
    return {
      userSettings: userSettingsCache.getStats(),
      projectMetadata: projectMetadataCache.getStats(),
      aiModelConfig: aiModelConfigCache.getStats(),
      session: sessionCache.getStats()
    };
  }
}

// Export default cache instance
const defaultCache = new CacheManager();
export default defaultCache;